import { DropZoneType, TiledDropZone } from "./DropZoneUtil";
import { hostname } from "os";
export { DropZoneType, TiledDropZone };

export class DropZoneConfig {
    public width?: number = 64;
    public height?: number = 64;
    public backgroundTexture?: string = null;
    public backgroundFrame?: string = null;
    // Default is no hotspot outline (lineColor), grey outline for dragLineColor (correct drop zone), and no outline for droppedLineColor
    public showBorder?: boolean = true;
    public lineColor?: number = null;
    public dragLineColor?: number = 0xa1b2a0;
    public droppedLineColor?: number = null;
    /**
     * Name scheme follows Tiled's schema. Will render a circle if true. If square is also true, then we will get angry
     */
    public ellipse?: boolean;
    /**
     * Name scheme follows Tiled's schema. Will render a square if true. If ellipse is also true, then we will get angry
     */
    public square?: boolean;
    public allowRedrop?: boolean = false; // Don't allow picking up the item after dropping it
    public allowStacking?: boolean = false; // Don't allow more than one item to be dropped on dropzone
    public allowResize?: boolean = true; // When dropping an item in dropzone, the item will be resized to fit the dropzone
    public allowSnapToCenter?: boolean = true; // When dropping an item in dropzone, the item will be snapped to the center of the dropzone
    public allowDropzoneDisappear?: boolean = true; // When dropping an item in dropzone, the dropzone image will disappear
    public allowSnapToAngle?: boolean = true; // When dropping an item in dropzone, item angle will snap to dropzone angle
    public acceptedKeys?: string[] = [];
    public debug?: boolean = false;
}

export class DropZone extends Phaser.GameObjects.Zone {
    config: DropZoneConfig;
    graphics: Phaser.GameObjects.Graphics;
    children: DropZoneType[];
    hotspot: Phaser.GameObjects.Image;

    constructor(scene: Phaser.Scene, x: number, y: number, options: DropZoneConfig) {
        // Options can be passed in not completely defined
        let config = { ...new DropZoneConfig(), ...options };

        // Guard against null and capital letters
        if (config.acceptedKeys == null) {
            config.acceptedKeys = [];
        } else {
            config.acceptedKeys = config.acceptedKeys.map(x => x.trim().toLowerCase());
        }

        if ((config.square && config.ellipse) || (!config.square && !config.ellipse)) {
            if (config.square) {
                // Throw error if 'both' were true
                console.warn("DropZone: Square and ellipse are provided, defaulting to square");
            }
            config.square = true;
            config.ellipse = false;
        }

        // Okay, NOW we can initialize
        super(scene, x, y, config.width, config.height);

        // Finish our initialization
        this.initialize(config);
        this.registerEvents();
    }

    initialize(config: DropZoneConfig): void {
        this.scene.add.existing(this);
        if (config.square) {
            // Square dropzones are generated from the top left
            let shape = new Phaser.Geom.Rectangle(-(this.width / 2), -(this.height / 2), this.width, this.height);
            this.setDropZone(shape, Phaser.Geom.Rectangle.Contains);
        } else if (config.ellipse) {
            // Circle dropzones are generated from a point in the center
            let shape = new Phaser.Geom.Ellipse(0, 0, this.width, this.height);
            this.setDropZone(shape, Phaser.Geom.Ellipse.Contains);
        }

        // We were passed an image for the dropzones (hotspot image)
        if (config.backgroundTexture) {
            let hotspot = this.scene.add.image(this.x - this.width / 2, this.y - this.height / 2, config.backgroundTexture, config.backgroundFrame);
            hotspot.setOrigin(0);
            //hotspot.setDepth(1);
            hotspot.setDisplaySize(config.width, config.height);
            this.hotspot = hotspot;
        }

        // For plain dropzones
        this.graphics = this.scene.add.graphics();

        this.config = config;
        this.children = [];
        this.setAngle(this.angle);
        this.setDepth(this.depth);
    }

    registerEvents() {
        // Grab the object that was dropped onto us
        this.scene.input.on('drop', (pointer, gameObject: DropZoneType, dropZone) => {
            // Check if this is the selected dropzone
            if (dropZone !== this) {
                return;
            }

            var accepted = this.acceptDrop(gameObject);
            if (!accepted) {
                this.emit('drop', this, gameObject, false);
                return;
            }

            if (this.config.allowSnapToCenter) {
                gameObject.x = this.x;
                gameObject.y = this.y;
            }

            this.children.push(gameObject);

            this.updateChildren();

            this.emit('drop', this, gameObject, true);

            // Make hotspot image disappear
            if (this.hotspot != null && this.config.allowDropzoneDisappear) {
                this.hotspot.setVisible(false);
            }

            this.updateOutline(this.config.droppedLineColor);

        });
    }

    /**
     * Check if we accept the drop
     */
    acceptDrop(gameObject: DropZoneType): boolean {
        // Do we have key checking enabled?
        var dropKeys = (gameObject.dropZoneKeys || []).map(x => x.toLowerCase()); // This also checks if object is of type DropZoneType
        if (this.config.acceptedKeys.length > 0 && !dropKeys.some(x => this.config.acceptedKeys.some(y => y === x))) {
            // Key not accepted
            return false;
        }
        // Do we allow stacking?
        if (this.children.length > 0 && !this.config.allowStacking) {
            return false;
        }
        return true;
    }

    // Updates the color of outline of the dropzones
    updateOutline(lineColor: number) {
        this.graphics.clear();
        this.graphics.lineStyle(2, lineColor);

        if (!this.config.showBorder || lineColor == null) {
            return;
        }

        if (this.config.square) {
            var topLeft = this.getTopLeft();
            var topRight = this.getTopRight();
            var bottomLeft = this.getBottomLeft();
            var bottomRight = this.getBottomRight();
            this.graphics.strokeRect(topLeft.x, topLeft.y, topRight.x - topLeft.x, bottomLeft.y - topLeft.y);
        } else if (this.config.ellipse) {
            this.graphics.strokeEllipse(this.x, this.y, this.width, this.height);
        }
    }

    protected updateChildren() {
        if (this.children.length === 0) {// || !this.config.allowResize) {
            return;
        }

        this.children.forEach(x => {
            if (this.config.allowSnapToAngle) {
                x.setAngle(this.angle);
            }
            x.setDepth(this.depth);
            if (this.config.allowResize) {
                x.setDisplaySize(this.displayWidth, this.displayHeight);
            }
            // Disallow moving it again if configured
            x.input.enabled = this.config.allowRedrop;
        });
    }

    setAngle(degrees?: number): this {
        super.setAngle(degrees);
        this.updateOutline(this.config.lineColor);
        this.updateChildren();
        return this;
    }

    setDepth(value: number): this {
        super.setDepth(value);
        this.graphics.setDepth(value);
        return this;
    }
}



// WEBPACK FOOTER //
// ./src/games/shared/dropzone/DropZone.ts